import React, { useCallback, useEffect, useState } from 'react';
import API from '../../services/api';
import './QuestionImage.css';

// Chart/graph questions (Data Interpretation) carry an asset key such as
// "charts/quant_c37_0001.png"; the API serves it read-only under /question-assets.
export const questionImageSrc = (imagePath) =>
  (typeof imagePath === 'string' && imagePath ? `${API.defaults.baseURL}/question-assets/${imagePath}` : null);

/**
 * The picture a question depends on. Renders nothing for an ordinary question.
 *  - a failed load shows a message with Retry (a chart question cannot be answered without its chart)
 *  - tap/click enlarges it IN the page (an overlay). Deliberately not "open in new tab":
 *    the exam screen treats leaving the tab as a violation.
 */
export default function QuestionImage({ path, compact = false }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [zoom, setZoom] = useState(false);
  const src = questionImageSrc(path);

  const close = useCallback(() => setZoom(false), []);
  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [zoom, close]);

  useEffect(() => { setFailed(false); setLoaded(false); setAttempt(0); }, [path]);

  if (!src) return null;
  const url = attempt ? `${src}?retry=${attempt}` : src;

  if (failed) {
    return (
      <div className="q-image q-image--error" role="alert">
        <span>The chart for this question could not be loaded.</span>
        <button type="button" className="q-image__retry" onClick={() => { setFailed(false); setLoaded(false); setAttempt(a => a + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <figure className={`q-image${compact ? ' q-image--compact' : ''}${loaded ? ' q-image--loaded' : ''}`} data-testid="question-image">
        <button type="button" className="q-image__btn" onClick={() => setZoom(true)} aria-label="Enlarge chart">
          <img
            src={url}
            alt="Chart or graph for this question"
            className="q-image__img"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            draggable={false}
          />
        </button>
        {!compact && <figcaption className="q-image__hint">Tap the chart to enlarge</figcaption>}
      </figure>
      {zoom && (
        <div className="q-image__overlay" role="dialog" aria-modal="true" aria-label="Chart, enlarged" onClick={close}>
          <div className="q-image__overlay-inner" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="q-image__close" onClick={close} aria-label="Close enlarged chart">×</button>
            <img src={url} alt="Chart or graph for this question, enlarged" className="q-image__zoomed" draggable={false} />
          </div>
        </div>
      )}
    </>
  );
}
